import { FC, useMemo, useState } from 'react';
import { RadixTable, TColumn, Button, Card } from '@features/ui';
import { ExportIcon } from '@features/ui/icons';
import { YearSelector } from '@features/admin/reports/components/YearSelector';
import {
  TBundlePAAccountMonthData,
  TBundlePAAccountPackageData,
} from '@src/types';
import {
  exportBundlePAAccounts,
  getBundlePAAccounts,
} from '@src/services/reports';
import { downloadFile, formatCurrency } from '@lib/utils.ts';

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const MonthCell: FC<{
  data?: TBundlePAAccountMonthData;
  isTotal?: boolean;
}> = ({ data, isTotal = false }) => {
  // Default values if no data
  const evalAccounts = data?.eval_accounts ?? 0;
  const paAccounts = data?.pa_accounts ?? 0;
  const payoutAccounts = data?.payout_accounts ?? 0;
  const paPercent = data?.pa_percent ?? '-';
  const payoutPercent = data?.payout_percent ?? '-';
  const payoutAmount = Number(data?.payout_total_amount ?? 0);
  const hasPercentData = paAccounts > 0 || payoutAccounts > 0;

  return (
    <div className="space-y-1">
      <div className={`flex gap-1 text-xs ${isTotal ? 'font-bold' : ''}`}>
        <span className="text-green-600 font-medium">{evalAccounts}</span>
        <span className="text-muted-foreground">/</span>
        <span className="text-yellow-600 font-medium">{paAccounts}</span>
        <span className="text-muted-foreground">/</span>
        <span className="text-blue-600 font-medium">{payoutAccounts}</span>
      </div>
      {hasPercentData && (
        <div
          className={`flex gap-2 text-[10px] text-muted-foreground ${isTotal ? 'font-semibold' : ''}`}
        >
          {paAccounts > 0 && <span>PA: {paPercent}</span>}
          {payoutAccounts > 0 && <span>Payout: {payoutPercent}</span>}
        </div>
      )}
      {payoutAmount > 0 && (
        <div
          className={`text-[10px] text-muted-foreground ${isTotal ? 'font-semibold' : ''}`}
        >
          Paid: ${formatCurrency(payoutAmount)}
        </div>
      )}
    </div>
  );
};

type TableRow = TBundlePAAccountPackageData & {
  isTotal?: boolean;
};

export const BundlesPAAccounts = () => {
  const [selectedYear, setSelectedYear] = useState<string>(
    new Date().getFullYear().toString()
  );
  const [data, setData] = useState<TableRow[]>([]);
  const [totals, setTotals] = useState<TBundlePAAccountMonthData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [showData, setShowData] = useState<boolean>(false);

  const handleGetData = async () => {
    setIsLoading(true);
    try {
      const response = await getBundlePAAccounts({
        year: Number(selectedYear),
      });

      setData(response.data);
      setTotals(response.totals);
      setShowData(true);
    } catch (error) {
      console.error('Failed to fetch bundle PA accounts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const response = await exportBundlePAAccounts({
        year: Number(selectedYear),
      });

      downloadFile(response.data, `bundle-pa-accounts-${selectedYear}`);
    } catch (error) {
      console.error('Failed to export bundle PA accounts:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const columns = useMemo((): TColumn<TableRow>[] => {
    const baseColumns: TColumn<TableRow>[] = [
      {
        title: 'Package',
        className: 'min-w-[300px] sticky left-0 z-10 bg-[#1a1a1a]',
        render: (item) => (
          <div className="bg-[#1a1a1a] pr-4">
            <p
              className={
                item?.isTotal
                  ? 'font-bold text-sm'
                  : 'text-xs whitespace-normal'
              }
            >
              {item?.package_name ?? '-'}
            </p>
          </div>
        ),
      },
    ];

    const monthColumns: TColumn<TableRow>[] = MONTHS.map((monthLabel) => ({
      title: (
        <div className="text-center">
          <div className="font-bold">{monthLabel}</div>
          <div className="text-[12px] font-normal">
            <span className="text-green-600">Evals</span> /{' '}
            <span className="text-yellow-600">PA</span> /{' '}
            <span className="text-blue-600">Payouts</span>
          </div>
        </div>
      ),
      className: 'text-center min-w-[150px]',
      render: (item) => {
        const monthData = item?.months?.find((m) => m.month === monthLabel);

        // Always show month cell, even if data is missing
        return <MonthCell data={monthData} isTotal={item?.isTotal} />;
      },
    }));

    return [...baseColumns, ...monthColumns];
  }, []);

  const displayData = useMemo(() => {
    if (!showData || data.length === 0) return [];

    const tableData: TableRow[] = [...data];

    // Add totals row
    if (totals.length > 0) {
      tableData.push({
        package_name: 'TOTALS',
        months: totals,
        isTotal: true,
      });
    }

    return tableData;
  }, [showData, data, totals]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
        <YearSelector
          maxYear={new Date().getFullYear()}
          minYear={2022}
          value={selectedYear}
          onChange={setSelectedYear}
        />
        <Button
          className="bg-[#B6D72C] text-black hover:bg-[#9fb824] px-6"
          disabled={isLoading}
          size="default"
          variant="default"
          onClick={handleGetData}
        >
          {isLoading ? 'Loading...' : 'Run Report'}
        </Button>
        <Button
          className={'h-9.5 border-1 border-[#C6C6C6] rounded-[3px] w-fit'}
          disabled={isExporting}
          variant={'outline'}
          onClick={handleExport}
        >
          <p>{isExporting ? 'Exporting...' : 'Export'}</p>
          <ExportIcon />
        </Button>
      </div>

      {showData && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">
              Bundles to PA&apos;s by Month {selectedYear}
            </h2>
            <p className="text-xs text-muted-foreground">
              All data is for &apos;accounts&apos;. Payouts is total amount paid
              out on PA&apos;s to-date.
            </p>
          </div>

          <Card className="bg-[#1a1a1a] border-[#3D3D3D]">
            <div className="overflow-x-auto">
              <RadixTable columns={columns} data={displayData} />
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};
